import { useCallback, useContext, useMemo } from "react";
import { ToastContext } from "./toastContext.tsx";

/**
 * Manage multiple toasts. You can use ids to control individual instances.
 * If ids are not provided, each show will create a new toast.
 *
 * IMPORTANT! showToast cannot access page-specific context (such as from
 * useLoaderData). Why?
 * - We aren't portaling the toasts, so the subcontext gets dropped
 * - If we were to portal the toasts they would unmount on page change, which
 *   is undesirable.
 * - If we had the context via another mechanism, it would still be destroyed
 *   when you navigated away, causing the toast to go boom in response to
 *   its dependency disappearing.
 * - Behaviorally, it is generally preferable to snapshot the data/message
 *   so it doesn't change unexpectedly. (While we do need updatable toasts
 *   at times, it is much less common than "show this specific message in
 *   response to this action".)
 *
 * @see useSingleToast
 * @param targetId
 */
export default function useToasts(targetId?: string) {
  const { toasts, showToast, closeToast } = useContext(ToastContext);

  const activeToasts = useMemo(() => {
    return toasts.filter((toast) => toast.targetId === targetId);
  }, [toasts, targetId]);

  const showToastWithTarget = useCallback<typeof showToast>(
    (toast, opts = {}) => {
      return showToast(toast, { id: opts.id, targetId });
    },
    [showToast, targetId]
  );

  return useMemo(
    () => ({
      toasts: activeToasts,
      showToast: showToastWithTarget,
      closeToast,
    }),
    [activeToasts, showToastWithTarget, closeToast]
  );
}
